/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import {
  Badge,
  Button,
  ButtonGroup,
  ButtonIcon,
  ButtonVariety,
  IconLinkExternal,
  IconX,
  ModalAlert,
  Text,
  TextSize,
  toast,
} from '@sonarsource/echoes-react';
import { isEmpty } from 'lodash';
import { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCurrentUser } from '~adapters/helpers/users';
import { isDefined } from '~shared/helpers/types';
import { getJiraPermissions, makeJiraPayload } from '../../helpers';
import {
  useCreateJiraWorkItemMutation,
  useGetJiraWorkItemQuery,
  useJiraProcessingWorkItemsQuery,
  useJiraUserActionsQuery,
  useJiraWorkTypesQuery,
  useUnlinkSonarIssueFromJiraWorkItemMutation,
} from '../../queries';
import { JiraProjectBinding, JiraWorkItemResourceType, LinkableIssue } from '../../types';
import JiraIcon from '../JiraIcon';
import { JiraWorkItemCreationButton } from './JiraWorkItemCreationButton';

function JiraLinkedWorkItemComponent(
  props: Readonly<{
    isDetailsPage: boolean;
    isEntitled: boolean;
    isLoadingExternal: boolean;
    issue: LinkableIssue;
    jiraBinding?: JiraProjectBinding;
    onSuccess?: () => void;
    orgUUID4?: string;
    resourceType: JiraWorkItemResourceType;
  }>,
) {
  const {
    issue,
    jiraBinding,
    orgUUID4,
    isDetailsPage,
    isEntitled,
    isLoadingExternal,
    resourceType,
    onSuccess,
  } = props;
  const resourceId = issue.key;
  const sonarProjectId = issue.projectUuid;
  const { formatMessage } = useIntl();
  const { currentUser, isLoggedIn } = useCurrentUser();
  const currentUserEmail = useMemo(() => {
    if (isLoggedIn) {
      return currentUser?.email ?? '';
    }
    return '';
  }, [isLoggedIn, currentUser]);

  const isProjectBound = !isEmpty(jiraBinding?.jiraProjectKey);
  const isJiraIntegrationEnabled = () => {
    return (
      // the project is bound to a Jira project
      isProjectBound &&
      // the organization has the UseJiraIntegration entitlement
      isEntitled
    );
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCreatingJiraWorkItem, setIsCreatingJiraWorkItem] = useState(false);

  const { data: userActions = [] } = useJiraUserActionsQuery(sonarProjectId, {
    enabled: isJiraIntegrationEnabled(),
  });

  const { data: processingWorkItems = {} } = useJiraProcessingWorkItemsQuery({
    enabled: isJiraIntegrationEnabled(),
  });

  const { hasJiraCreatePermission, hasJiraDeletePermission } = getJiraPermissions(userActions);

  const { mutate: unlinkIssueFromJiraWorkItemMutation, isPending: isDeletingWorkItem } =
    useUnlinkSonarIssueFromJiraWorkItemMutation();

  const jiraButtonIsLoading =
    isDefined(processingWorkItems[issue.key]) || isCreatingJiraWorkItem || isLoadingExternal;

  const { data: jiraWorkTypes = [] } = useJiraWorkTypesQuery(
    {
      sonarProjectId,
      includeFields: false,
      jiraProjectKey: jiraBinding?.jiraProjectKey ?? '',
      sonarOrganizationUuid: orgUUID4 ?? '',
    },
    {
      enabled: isJiraIntegrationEnabled() && hasJiraCreatePermission && Boolean(orgUUID4),
    },
  );

  const { mutate: createJiraWorkItemMutation } = useCreateJiraWorkItemMutation();

  const {
    data: linkedWorkItem,
    error: isError,
    refetch: refetchLinkedWorkItem,
  } = useGetJiraWorkItemQuery(
    {
      sonarProjectId,
      resourceId,
      resourceType,
    },
    { enabled: isJiraIntegrationEnabled() },
  );

  useEffect(() => {
    // Only refetch if the work item is coming from bulk endpoint
    // (which did not retrieve the 'status')
    if (isDetailsPage && isProjectBound && !isDefined(linkedWorkItem?.jiraIssueStatus)) {
      void refetchLinkedWorkItem();
    }
  }, [isDetailsPage, isProjectBound, linkedWorkItem?.jiraIssueStatus, refetchLinkedWorkItem]);

  const handleCreateJiraWorkItem = useCallback(
    (workTypeId: string) => {
      setIsCreatingJiraWorkItem(true);
      const jiraWorkItemPayload = makeJiraPayload({
        email: currentUserEmail,
        isLoggedIn,
        issue,
        workTypeId,
        resourceType,
      });

      createJiraWorkItemMutation(jiraWorkItemPayload, {
        onSuccess: (workItem) => {
          setIsCreatingJiraWorkItem(false);
          toast.success({
            description: (
              <FormattedMessage
                id="issue.jira.work_item_creation_success"
                values={{ 0: workItem.jiraIssueKey }}
              />
            ),
            duration: 'short',
          });
          onSuccess?.();
        },
        onSettled: () => {
          setIsCreatingJiraWorkItem(false);
        },
      });
    },
    [currentUserEmail, isLoggedIn, issue, resourceType, createJiraWorkItemMutation, onSuccess],
  );

  const handleDeleteJiraWorkItem = () => {
    if (!linkedWorkItem) {
      return;
    }

    unlinkIssueFromJiraWorkItemMutation(
      {
        sonarProjectId,
        resourceId,
        resourceType,
      },
      {
        onSuccess: () => {
          setIsDeleteModalOpen(false);
          toast.success({
            description: (
              <FormattedMessage
                id="issue.jira.work_item_deletion_success"
                values={{ 0: linkedWorkItem.jiraIssueKey }}
              />
            ),
            duration: 'short',
          });
        },
        onError: () => {
          toast.error({
            description: (
              <FormattedMessage
                id="issue.jira.work_item_deletion_error"
                values={{ 0: linkedWorkItem.jiraIssueKey }}
              />
            ),
            duration: 'short',
          });
        },
      },
    );
  };

  const workItemButton = (
    <Button
      onClick={() => window.open(linkedWorkItem?.jiraIssueUrl, '_blank', 'noopener,noreferrer')}
      prefix={<JiraIcon />}
      suffix={
        <>
          <IconLinkExternal />
          {linkedWorkItem?.jiraIssueStatus && (
            <Badge variety="neutral">{linkedWorkItem.jiraIssueStatus}</Badge>
          )}
        </>
      }
    >
      <Text size={TextSize.Default}>{linkedWorkItem?.jiraIssueKey}</Text>
    </Button>
  );

  if (linkedWorkItem?.jiraIssueKey && !isError) {
    if (!hasJiraDeletePermission) {
      return workItemButton;
    }

    return (
      <ButtonGroup isCombined>
        <Fragment key=".0">
          {workItemButton}
          <ButtonIcon
            Icon={IconX}
            ariaLabel={formatMessage({ id: 'issue.jira.work_item_deletion_tooltip' })}
            onClick={() => {
              setIsDeleteModalOpen(true);
            }}
          />
          <ModalAlert
            description={formatMessage({
              id: 'issue.jira.work_item_deletion_modal_description',
            })}
            isOpen={isDeleteModalOpen}
            onOpenChange={setIsDeleteModalOpen}
            primaryButton={
              <Button
                isDisabled={isDeletingWorkItem}
                isLoading={isDeletingWorkItem}
                onClick={handleDeleteJiraWorkItem}
                variety={ButtonVariety.Danger}
              >
                <FormattedMessage id="issue.jira.work_item_deletion_link" />
              </Button>
            }
            secondaryButtonLabel={formatMessage({ id: 'cancel' })}
            title={formatMessage({ id: 'issue.jira.work_item_deletion_modal_title' })}
          />
        </Fragment>
      </ButtonGroup>
    );
  }

  return (
    <JiraWorkItemCreationButton
      isLoading={jiraButtonIsLoading}
      jiraWorkTypes={jiraWorkTypes}
      onCreateWorkItem={handleCreateJiraWorkItem}
    />
  );
}

export const JiraLinkedWorkItem = memo(JiraLinkedWorkItemComponent);
